import { forwardRef } from 'react';
import {
  Switch as MuiSwitch,
  SwitchProps as MuiSwitchProps,
  FormControlLabel,
  FormControlLabelProps,
} from '@mui/material';

/**
 * Switch component - A toggle control that extends MUI Switch with FormControlLabel integration.
 *
 * **Features:**
 * - Required label for accessibility (FormControlLabel wrapper)
 * - Simplified onChange: receives boolean value instead of event
 * - Flexible label placement (start, end, top, bottom)
 * - Pass-through switchProps for advanced MUI Switch customization
 * - Testing attributes support (data-testid, data-identifier)
 * - Forward ref support for imperative access
 * - Full accessibility support (ARIA attributes, keyboard interaction)
 *
 * **Common Use Cases:**
 * - Toggle settings and preferences
 * - Enable/disable features
 * - Binary form inputs
 * - On/off state controls
 *
 * @example
 * ```tsx
 * // Basic switch
 * <Switch label="Enable notifications" checked={enabled} onChange={setEnabled} />
 *
 * // With label on start (mobile-friendly)
 * <Switch
 *   label="Haptic feedback"
 *   labelPlacement="start"
 *   checked={haptics}
 *   onChange={setHaptics}
 * />
 *
 * // Disabled state
 * <Switch label="Premium feature" checked={false} disabled={!isPremium} />
 *
 * // With custom MUI Switch props
 * <Switch
 *   label="Dark mode"
 *   checked={darkMode}
 *   onChange={setDarkMode}
 *   switchProps={{ color: 'secondary', size: 'small' }}
 * />
 *
 * // For testing
 * <Switch
 *   label="Feature toggle"
 *   checked={feature}
 *   onChange={setFeature}
 *   data-testid="feature-toggle"
 *   data-identifier="settings.feature"
 * />
 * ```
 */
export interface SwitchProps
  extends Omit<FormControlLabelProps, 'control' | 'checked' | 'onChange'> {
  /**
   * If true, the switch is checked.
   * Use this for controlled components where parent manages state.
   */
  checked?: boolean;

  /**
   * Callback fired when the switch is toggled.
   * Receives the new checked state as a boolean (not an event object).
   *
   * @param checked - The new checked state (true or false)
   *
   * @example
   * ```tsx
   * const [enabled, setEnabled] = useState(false);
   * <Switch label="Enable" checked={enabled} onChange={setEnabled} />
   * ```
   */
  onChange?: (checked: boolean) => void;

  /**
   * If true, the switch will be disabled.
   * Disabled switches cannot be interacted with and have reduced opacity.
   */
  disabled?: boolean;

  /**
   * The label for the switch.
   * Required for accessibility - ensures all switches have descriptive text.
   */
  label: string;

  /**
   * The position of the label relative to the switch control.
   *
   * @default 'end'
   *
   * @example
   * ```tsx
   * // Label on right (default)
   * <Switch label="Feature" labelPlacement="end" />
   *
   * // Label on left (common in settings)
   * <Switch label="Setting" labelPlacement="start" />
   *
   * // Label above/below (less common)
   * <Switch label="Option" labelPlacement="top" />
   * ```
   */
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';

  /**
   * Additional props to pass directly to the MUI Switch component.
   * Use this for advanced customization like color, size, or icon overrides.
   *
   * @example
   * ```tsx
   * <Switch
   *   label="Custom"
   *   switchProps={{
   *     color: 'secondary',
   *     size: 'small',
   *   }}
   * />
   * ```
   */
  switchProps?: MuiSwitchProps;

  /**
   * Custom data attribute for testing.
   * Applied to the underlying MUI Switch input element.
   *
   * @example
   * ```tsx
   * <Switch label="Feature" data-testid="feature-toggle" />
   * ```
   */
  'data-testid'?: string;

  /**
   * Custom data identifier attribute.
   * Useful for tracking field identity in analytics or E2E tests.
   *
   * @example
   * ```tsx
   * <Switch label="Setting" data-identifier="user.settings.notifications" />
   * ```
   */
  'data-identifier'?: string;

  /**
   * Additional sx styles applied to the FormControlLabel wrapper.
   *
   * @example
   * ```tsx
   * <Switch
   *   label="Styled"
   *   sx={{ marginBottom: 2, '& .MuiSwitch-root': { marginRight: 1 } }}
   * />
   * ```
   */
  sx?: FormControlLabelProps['sx'];
}

export const Switch = forwardRef<HTMLLabelElement, SwitchProps>(
  (
    {
      checked,
      onChange,
      disabled,
      label,
      labelPlacement = 'end',
      switchProps,
      'data-testid': dataTestId,
      'data-identifier': dataIdentifier,
      sx,
      ...props
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event.target.checked);
      }
    };

    return (
      <FormControlLabel
        ref={ref}
        control={
          <MuiSwitch
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            data-testid={dataTestId}
            data-identifier={dataIdentifier}
            {...switchProps}
          />
        }
        label={label}
        labelPlacement={labelPlacement}
        disabled={disabled}
        sx={sx}
        {...props}
      />
    );
  }
);

Switch.displayName = 'Switch';
